import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { toastSuccess } from '@components/Toast';

import { fetchProfitMarginTableDataById } from '@store/actions/BrandProfileAction';

import { updateProfitMarginData } from '@services/crmService';

import { replaceUnderLine } from '@utils/Util';

const PriceTable = ({
	extraClass,
	tableData,
	fabricTypes,
	marketsForFabricTypes,
	isEditable,
	onEditClick,
	onDeleteClick
}) => {
	const [editingCell, setEditingCell] = useState(null);
	const [inputValue, setInputValue] = useState(null);
	const inputRef = useRef(null);
	const dispatch = useDispatch();
	const { id } = useParams();

	useEffect(() => {
		if (editingCell && inputRef.current) {
			inputRef.current.focus();
		}
	}, [editingCell]);

	const handleCellClick = (categoryId, fabricType, marketId, currentValue) => {
		setEditingCell({ categoryId, fabricType, marketId });
		setInputValue(currentValue || '');
	};

	const handleInputChange = (e) => {
		setInputValue(e.target.value);
	};

	const handleBlur = async () => {
		if (editingCell) {
			const { categoryId, fabricType, marketId } = editingCell;

			const prevInputValue = tableData
				.find((category) => category.parentCategoryResponse.constant === categoryId)
				?.fabricWiseMarginList.find((f) => f.fabricType === fabricType)
				?.marginList.find((m) => m.marketResponse.id === marketId)?.percentage;

			if (inputValue === '' || inputValue === null || inputValue === undefined) {
				setEditingCell(null);
				return;
			}

			if (Number(inputValue) !== prevInputValue) {
				let postData = {
					brandId: Number(id),
					parentCatergory: categoryId,
					fabricType: fabricType,
					marketId: marketId,
					percentage: Number(inputValue)
				};

				const data = await updateProfitMarginData(id, postData);
				if (data?.success) {
					toastSuccess('Successfully updated');
				}
				dispatch(fetchProfitMarginTableDataById(`${id}`));
			}

			setEditingCell(null);
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			handleBlur();
		}
	};

	const renderCellContent = (categoryId, fabricType, market, percentage) => {
		const isCurrentEditing =
			editingCell &&
			editingCell.categoryId === categoryId &&
			editingCell.fabricType === fabricType &&
			editingCell.marketId === market.id;

		if (isCurrentEditing) {
			return (
				<input
					ref={inputRef}
					type="number"
					value={inputValue}
					onChange={handleInputChange}
					onBlur={handleBlur}
					onKeyDown={handleKeyDown}
				/>
			);
		}

		return <p className="gray_dark_01">{percentage !== undefined ? `${percentage}%` : ''}</p>;
	};

	const renderPriceRangeTableCell = (category) => {
		return fabricTypes?.map((fabricType) =>
			marketsForFabricTypes[fabricType]?.map((market) => {
				const fabric = category.fabricPriceRangeList?.find((f) => f.fabricType === fabricType);

				const priceRange = fabric
					? fabric.priceRangeList?.find((p) => p.marketResponse.id === market.id)
					: null;

				return (
					<td key={`${fabricType}_${market.id}`}>
						<p className="gray_dark_01">
							{priceRange?.id ? `$${priceRange.minPrice}-$${priceRange.maxPrice}` : ''}
						</p>
					</td>
				);
			})
		);
	};

	const renderProfitMarginTableCell = (category) => {
		return fabricTypes?.map((fabricType) =>
			marketsForFabricTypes[fabricType]?.map((market) => {
				const fabric = category.fabricWiseMarginList?.find((f) => f.fabricType === fabricType);
				const margin = fabric
					? fabric.marginList?.find((m) => m.marketResponse.id === market.id)
					: null;

				return (
					<td
						key={`${fabricType}_${market.id}`}
						onClick={() =>
							isEditable &&
							handleCellClick(
								category?.parentCategoryResponse?.constant,
								fabricType,
								market.id,
								margin?.percentage
							)
						}
					>
						{renderCellContent(
							category?.parentCategoryResponse?.constant,
							fabricType,
							market,
							margin?.percentage
						)}
					</td>
				);
			})
		);
	};

	return (
		<table className={`table with-border height-sm price-configuration-table ${extraClass}`}>
			<thead>
				<tr>
					<th rowSpan={2} className="sticky-category">
						Category Name
					</th>
					{fabricTypes.map((fabricType, index) => (
						<th
							key={index}
							colSpan={marketsForFabricTypes[fabricType]?.length}
							className="text-center"
						>
							{replaceUnderLine(fabricType)}
						</th>
					))}
					{!isEditable && <th></th>}
				</tr>
				<tr>
					{fabricTypes.map((fabricType, index) =>
						marketsForFabricTypes[fabricType]?.map((market, idx) => (
							<th key={`${fabricType}_${market.id}_${index}_${idx}`}>{market.value}</th>
						))
					)}
					{!isEditable && <th></th>}
				</tr>
			</thead>
			<tbody>
				{tableData?.map((category, rowIdx) => (
					<tr key={rowIdx}>
						<td className="sticky-category">
							<div className="d-flex justify-content-between">
								{isEditable
									? category?.parentCategoryResponse?.name
									: category?.categoryResponse?.name}
							</div>
						</td>
						{isEditable
							? renderProfitMarginTableCell(category)
							: renderPriceRangeTableCell(category)}

						{!isEditable && (
							<td>
								<div className="option">
									<div className="dropdown">
										<button
											className="btn btn-default dropdown-toggle"
											type="button"
											id="menu1"
											data-toggle="dropdown"
											aria-expanded="false"
										>
											<img
												src={`${process.env.PUBLIC_URL}/icons/more_icon.svg`}
												alt="options"
											/>
										</button>
										<ul
											className="dropdown-menu dropdown-menu-right shadow-lg"
											role="menu"
											aria-labelledby="menu1"
											x-placement="bottom-end"
										>
											{!isEditable && (
												<li onClick={() => onEditClick(category.categoryResponse.id)}>
													<span>Edit</span>
												</li>
											)}
											<li onClick={() => onDeleteClick(category.categoryResponse.id)}>
												<span className="red">Delete</span>
											</li>
										</ul>
									</div>
								</div>
							</td>
						)}
					</tr>
				))}
			</tbody>
		</table>
	);
};

export default PriceTable;
